import { useCallback, useEffect, useRef, useState } from "react";
import { Rocket, Scroll, User } from "@phosphor-icons/react";
import { LayoutGrid } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Typography from "@/components/ui/typography";
import { cn } from "@/lib/utils";

interface FeaturedAgent {
  id: string;
  name: string;
  icon: React.ReactNode;
  prompt: string;
  bgColor: string;
  textColor: string;
}

// Onboarding agent shown only to non-onboarded users
const onboardingAgent: FeaturedAgent = {
  id: "onboarding-guide",
  name: "Getting Started",
  icon: <Rocket size={14} weight="duotone" />,
  prompt: "Let's get started with my onboarding.",
  bgColor: "bg-gradient-to-br from-emerald-100 via-green-50 to-teal-100",
  textColor: "text-emerald-600",
};

// Featured agents shown to onboarded users
const featuredAgents: FeaturedAgent[] = [
  {
    id: "linkedin-profile-optimizer",
    name: "LinkedIn Profile Optimizer",
    icon: <User size={14} weight="duotone" />,
    prompt: "Optimize my LinkedIn profile to attract AI consulting clients",
    bgColor: "bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-100",
    textColor: "text-amber-600",
  },
  {
    id: "project-proposal-generator",
    name: "Project Proposal Generator",
    icon: <Scroll size={14} weight="duotone" />,
    prompt: "Help me create a professional project proposal for a client",
    bgColor: "bg-gradient-to-br from-blue-100 via-sky-50 to-cyan-100",
    textColor: "text-blue-600",
  },
];

interface AgentSuggestionsProps {
  onAgentSelect: (prompt: string) => void;
  isOnboarded?: boolean;
}

export const AgentSuggestions = ({
  onAgentSelect,
  isOnboarded = false,
}: AgentSuggestionsProps) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const handleViewAllAgents = () => {
    navigate("/stacey-library");
  };

  // Check scroll position to show/hide fades
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const isScrollable = scrollWidth > clientWidth;

    setShowLeftFade(scrollLeft > 0);
    setShowRightFade(isScrollable && scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Check on mount and when content changes
  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [checkScroll, isOnboarded]);

  // Show onboarding agent for non-onboarded users, featured agents for onboarded users
  const visibleAgents = isOnboarded ? featuredAgents : [onboardingAgent];

  return (
    <div className="relative bg-gradient-to-b from-gray-50 to-gray-100/50 rounded-b-2xl border border-gray-200 border-t-0 -mt-[1px]">
      {/* Scrollable container with equal vertical padding */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex items-center gap-2 overflow-x-auto scrollbar-hide px-3 py-2.5 md:px-4 md:py-3"
      >
        {/* All agents button FIRST */}
        <button
          onClick={handleViewAllAgents}
          className={cn(
            "inline-flex items-center gap-2 px-3 py-1.5 md:py-2 rounded-full border border-gray-200 bg-white flex-shrink-0",
            "transition-all duration-200 ease-in-out",
            "hover:shadow-md hover:border-gray-300",
          )}
        >
          <LayoutGrid className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <Typography size="b3" className="text-gray-600 font-medium whitespace-nowrap">
            All agents
          </Typography>
        </button>
        {visibleAgents.map((agent) => (
          <button
            key={agent.id}
            onClick={() => onAgentSelect(agent.prompt)}
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1.5 md:py-2 rounded-full border border-gray-200 bg-white flex-shrink-0",
              "transition-all duration-200 ease-in-out",
              "hover:shadow-md hover:border-gray-300",
            )}
          >
            <div
              className={cn(
                "flex items-center justify-center rounded-md w-5 h-5 flex-shrink-0",
                "shadow-sm ring-1 ring-black/5",
                agent.bgColor,
                agent.textColor,
              )}
            >
              {agent.icon}
            </div>
            <Typography size="b3" className="text-gray-600 font-medium whitespace-nowrap">
              {agent.name}
            </Typography>
          </button>
        ))}
      </div>
      {/* Left fade gradient */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-gray-100 to-transparent pointer-events-none rounded-bl-2xl transition-opacity duration-200",
          showLeftFade ? "opacity-100" : "opacity-0",
        )}
      />
      {/* Right fade gradient */}
      <div
        className={cn(
          "absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-gray-100 to-transparent pointer-events-none rounded-br-2xl transition-opacity duration-200",
          showRightFade ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
};
